
import React from "react";
import TenantBooking from "./TenantBooking";
import {
  DAY_WIDTH,
  getBookingPosition,
} from "../utils/timelineUtils";

const BOOKING_HEIGHT = 58;
const LANE_GAP = 4;
const TRACK_PADDING = 4;
const MIN_ROW_HEIGHT = 50;

export default function RoomRow({
  room,
  stays = [],
  dates = [],
  laneCount = 1,
  onBookingClick,
  onBookingDoubleClick,
  onBookingContext,
  onRoomContext,
  onRoomClick,
  onDropStay,
  onResizeStay,
}) {
  if (!dates.length) return null;

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const start = formatLocalDate(dates[0]);

  const roomId = String(
    room.id ?? room.roomRowId ?? room.roomNumber ?? ""
  );

  const roomNumber = String(
    room.number ?? room.roomNumber ?? ""
  ).trim();

  const roomCapacity = room.capacity ?? room.roomCapacity ?? 0;

  const roomStatus = String(
    room.status ?? "AVAILABLE"
  ).toLowerCase();

  const rowStays = stays
  .map((stay) => ({
    ...stay,
    id: stay.id ?? stay.stayId,
    roomId: String(stay.roomId ?? "").trim(),
    roomNumber: String(stay.roomNumber ?? "").trim(),
  }))
  .filter((stay) => {
    const status = String(stay.status || "").toUpperCase();

    if (status === "CANCELLED") {
      return false;
    }

    // If roomId exists, match strictly by roomId.
    if (stay.roomId) {
      return stay.roomId === roomId.trim();
    }

    // Use roomNumber only when roomId is missing.
    return (
      roomNumber !== "" &&
      stay.roomNumber !== "" &&
      stay.roomNumber === roomNumber
    );
  });

  const requiredLanes = Math.max(
    1,
    laneCount,
    ...rowStays.map((stay) => (Number(stay._lane) || 0) + 1)
  );

  const rowHeight = Math.max(
    MIN_ROW_HEIGHT,
    requiredLanes * (BOOKING_HEIGHT + LANE_GAP) +
      TRACK_PADDING * 2
  );

  return (
    <div
      className="occ-room-row"
      style={{
        height: rowHeight,
        minHeight: rowHeight,
        boxSizing: "border-box",
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();

        const id = event.dataTransfer.getData(
          "text/occupancy-stay"
        );

        if (id && roomId) {
          onDropStay?.(id, room.id ?? room.roomRowId);
        }
      }}
    >
     
<button
  type="button"
  className="occ-room-cell"
  style={{
    height: rowHeight,
    minHeight: rowHeight,
  }}
  onClick={() => onRoomClick?.(room)}
  onContextMenu={(event) => {
    event.preventDefault();
    onRoomContext?.(event, room);
  }}
  title={`Room ${roomNumber || "Unassigned"} · ${roomCapacity} ${
    Number(roomCapacity) === 1 ? "guest" : "guests"
  } · ${roomStatus}`}
>
  <span className="occ-room-primary">
    <i
      className={`occ-room-status status-${roomStatus}`}
      aria-hidden="true"
    />

    <span className="occ-room-info">
      <b className="occ-room-number">
        {roomNumber || "Unassigned"}
      </b>

      <small className="occ-room-type">
        {room.roomTypeName ||
          room.roomType ||
          room.typeName ||
          room.type ||
          "Guest Room"}
      </small>
    </span>
  </span>

  <span className="occ-room-capacity">
    <span className="occ-room-capacity-icon" aria-hidden="true">
      ♙
    </span>
    {roomCapacity}{" "}
    {Number(roomCapacity) === 1 ? "guest" : "guests"}
  </span>
</button>


      <div
        className="occ-room-track"
        style={{
          position: "relative",
          width: dates.length * DAY_WIDTH,
          height: rowHeight,
          minHeight: rowHeight,
          boxSizing: "border-box",
        }}
      >
        {dates.map((date, index) => (
          <div
            key={formatLocalDate(date)}
            className={`occ-grid-day ${
              formatLocalDate(date) ===
              formatLocalDate(new Date())
                ? "is-today"
                : ""
            }`}
            style={{
              position: "absolute",
              left: index * DAY_WIDTH,
              width: DAY_WIDTH,
              minWidth: DAY_WIDTH,
              maxWidth: DAY_WIDTH,
              height: "100%",
              boxSizing: "border-box",
            }}
          />
        ))}

        {rowStays.map((stay) => {
          const position = getBookingPosition(
            stay,
            start,
            dates.length
          );

          console.log("[RoomRow] Booking calculation", {
            stayId: stay.id,
            roomNumber: stay.roomNumber,
            checkIn: stay.checkIn,
            checkOut: stay.checkOut,
            timelineStart: start,
            firstVisibleDate: formatLocalDate(dates[0]),
            lastVisibleDate: formatLocalDate(
              dates[dates.length - 1]
            ),
            visibleDays: dates.length,
            dayWidth: DAY_WIDTH,
            calculatedLeft: position?.left,
            calculatedWidth: position?.width,
            calculatedRight: position
              ? position.left + position.width
              : null,
          });

          if (!position || position.width <= 0) {
            return null;
          }

          const lane = Math.max(
            0,
            Number(stay._lane) || 0
          );

          const top =
            TRACK_PADDING +
            lane * (BOOKING_HEIGHT + LANE_GAP);

          return (
            <TenantBooking
              key={stay.id}
              stay={stay}
              left={position.left}
              top={top}
              width={position.width}
              room={room}
              onClick={onBookingClick}
              onDoubleClick={onBookingDoubleClick}
              onContextMenu={onBookingContext}
              onResize={onResizeStay}
            />
          );
        })}
      </div>
    </div>
  );
}
